import type { BillingAccount } from '@ctct/domain-layer';
import { formValueSessionStorage } from '@ctct/infrastructure-layer';
import type { BillingInfoFormPageStore } from '@ctct/state-client-layer';
import { createBillingInfoFormSliceDefaultValues } from '@ctct/state-client-layer';

import type { UIService } from '../../_shared/Form/services/UIService';
import { billingInfoFormEventBus } from '../BillingInfoFormEventBus';
import type { BillingInfoFormValues } from '../types';

export class FormMountService {
  constructor(
    private pageStore: BillingInfoFormPageStore,
    private getDefaultValues: () => BillingInfoFormValues,
    private uiService: UIService<BillingInfoFormValues>,
    private validate: (values: BillingInfoFormValues) => boolean,
    private billingAccount?: BillingAccount
  ) {}

  public api() {
    return {
      resetState: () => this.initFormValues(),

      allRequiredFieldsAreFilledWithDefaultValues: () => {
        return this.validate(this.getDefaultValues());
      },

      clearBillingFormData: () =>
        formValueSessionStorage.clearPayflowBillingFormData(this.billingAccount?.getCustomerId?.()),
    };
  }

  public data() {
    return {
      getDefaultValues: () => this.getDefaultValues(),
    };
  }

  public handlers() {
    return {
      persistBillingFormData: () => this.persistFormValuesIfAvailable(),
    };
  }

  public initFormValues() {
    const initState = createBillingInfoFormSliceDefaultValues();

    const defaultValues = this.getDefaultValues();
    const allRequiredFieldsAreFilled = this.validate(defaultValues);

    if (!allRequiredFieldsAreFilled) {
      initState.billingInfoFormCanDisplayCard = false;
      initState.billingInfoFormIsDisplayingCard = false;
    }

    const missingFields = Object.keys(defaultValues)
      .filter((key) => defaultValues[key] === '' || defaultValues[key] === null)
      .map((key) => key as keyof BillingInfoFormValues);

    billingInfoFormEventBus.emit('missingFields', missingFields, { source: 'onMount' });

    this.pageStore.setState(initState);
  }

  private persistFormValuesIfAvailable() {
    const formData = this.uiService.getFormValuesFn?.();

    if (!formData) {
      return;
    }

    formValueSessionStorage.savePayflowBillingFormData(formData, this.billingAccount?.getCustomerId?.());
  }
}
